import type { JsonObject, JsonValue } from "./primitives";

/**
 * RFC-6901 JSON Pointer read/write helpers over plain JSON (`JsonValue`).
 *
 * These are the shared, pure, structural-sharing primitives behind the
 * strategy-builder's `edit_strategy` tool: the server (`@richrich/api`) applies
 * a model's edits to the persisted `draft_dsl`, and the web client folds the same
 * edits into the live right-panel document as they stream. Keeping one
 * implementation here (contracts has zero runtime deps, and web/api/ai all depend
 * on it) means both sides walk pointers identically — no client/server drift.
 *
 * Nothing here throws on a partial/streaming document: a pointer that runs through
 * a non-container is a no-op, so a half-applied edit never blanks the panel. The
 * server re-parses the whole document with `StrategyDSLSchema` before anything
 * becomes real, so these helpers are a rendering/patch convenience, never a trust
 * boundary.
 */

/** A parsed JSON Pointer as its decoded reference tokens (RFC 6901). */
export type JsonPointer = readonly string[];

/**
 * One `edit_strategy` edit: an RFC-6901 pointer `path` and an optional `value`.
 * `value` PRESENT ⇒ idempotent subtree replace at `path`; `value` ABSENT ⇒ remove
 * the node at `path`. The absent-vs-`null` distinction is by key presence
 * (`"value" in edit`), so a present `value: null` sets JSON null (not a removal).
 * The `| undefined` widening only mirrors what Zod's `.optional()` infers under
 * `exactOptionalPropertyTypes`; JSON tool input can never carry an actual
 * `undefined` value.
 */
export interface StrategyEdit {
  readonly path: string;
  readonly value?: JsonValue | undefined;
}

/** Decode a JSON Pointer string (`/rules/0/id`) into reference tokens. */
export function parsePointer(pointer: string): JsonPointer {
  if (pointer === "") return [];
  if (!pointer.startsWith("/")) return pointer.split("/").map(decodeToken);
  return pointer.slice(1).split("/").map(decodeToken);
}

function decodeToken(token: string): string {
  return token.replace(/~1/g, "/").replace(/~0/g, "~");
}

/** Read the value at a pointer, or `undefined` when the path is absent. */
export function getAtPointer(
  doc: JsonValue | undefined,
  pointer: JsonPointer,
): JsonValue | undefined {
  let node: JsonValue | undefined = doc;
  for (const token of pointer) {
    if (node === null || node === undefined) return undefined;
    if (Array.isArray(node)) {
      const index = token === "-" ? node.length : Number(token);
      if (!Number.isInteger(index) || index < 0 || index >= node.length) return undefined;
      node = node[index];
    } else if (typeof node === "object") {
      node = (node as JsonObject)[token];
    } else {
      return undefined;
    }
  }
  return node;
}

/**
 * Return a copy of `doc` with `value` set at `pointer`, creating intermediate
 * objects as needed. Structural sharing: only nodes on the path are recreated.
 * A pointer that runs through a non-container (e.g. a string) is a no-op. An
 * empty pointer replaces the whole document — callers that must forbid a
 * whole-document write (the `edit_strategy` back door) reject it before here.
 */
export function setAtPointer(
  doc: JsonValue | undefined,
  pointer: JsonPointer,
  value: JsonValue,
): JsonValue {
  if (pointer.length === 0) return value;
  const [head, ...rest] = pointer;
  if (head === undefined) return value;

  // Array container.
  if (Array.isArray(doc)) {
    const index = head === "-" ? doc.length : Number(head);
    if (!Number.isInteger(index) || index < 0) return doc;
    const next = doc.slice();
    next[index] = setAtPointer(next[index], rest, value);
    return next;
  }

  // Object container (or create one when the node is missing/primitive).
  const base: JsonObject =
    doc !== null && typeof doc === "object" ? { ...(doc as JsonObject) } : {};
  base[head] = setAtPointer(base[head], rest, value);
  return base;
}

/** Return a copy of `doc` with the node at `pointer` removed (no-op if absent). */
export function removeAtPointer(
  doc: JsonValue | undefined,
  pointer: JsonPointer,
): JsonValue | undefined {
  if (pointer.length === 0) return undefined;
  if (doc === null || typeof doc !== "object") return doc;
  const [head, ...rest] = pointer;
  if (head === undefined) return doc;

  if (Array.isArray(doc)) {
    const index = Number(head);
    if (!Number.isInteger(index) || index < 0 || index >= doc.length) return doc;
    if (rest.length === 0) {
      const next = doc.slice();
      next.splice(index, 1);
      return next;
    }
    const next = doc.slice();
    next[index] = removeAtPointer(next[index], rest) as JsonValue;
    return next;
  }

  const obj = doc as JsonObject;
  if (!(head in obj)) return doc;
  if (rest.length === 0) {
    const next = { ...obj };
    delete next[head];
    return next;
  }
  return { ...obj, [head]: removeAtPointer(obj[head], rest) as JsonValue };
}

/**
 * Apply an ordered list of {@link StrategyEdit}s to a document, returning a new
 * document (structural sharing). Best-effort and total: an edit whose pointer
 * walks through a non-container is a no-op per {@link setAtPointer}. An edit with
 * an EMPTY pointer (a whole-document write) is SKIPPED — the root is never
 * writable through this path (the schema also rejects `""`/`"/"`), closing the
 * "replace the entire strategy" back door. `value` present ⇒ set, absent ⇒ remove.
 */
export function applyEditsToDoc(doc: JsonObject, edits: readonly StrategyEdit[]): JsonObject {
  let current: JsonValue = doc;
  for (const edit of edits) {
    const pointer = parsePointer(edit.path);
    if (pointer.length === 0) continue; // root write forbidden (back door)
    if ("value" in edit) {
      current = setAtPointer(current, pointer, edit.value as JsonValue);
    } else {
      const removed = removeAtPointer(current, pointer);
      if (removed !== undefined) current = removed;
    }
  }
  return (
    current !== null && typeof current === "object" && !Array.isArray(current) ? current : doc
  ) as JsonObject;
}
